import { useEffect, useContext, useState } from "react";

//redux
import { useAppDispatch, useAppSelector } from "../hooks/reduxHooks";
import { updatePageButtons, updatePageTitle } from "../store/layout";
import { updateThemeName, setThemeTypeDark, setThemeTypeLight } from "../store/theme";
import { updateAlarmEnabled, updateAlarmThreshold, updateDefaultAlarmSound } from "../store/settings";

//material-ui
import {
	Box,
	Button,
	Divider,
	FormControl,
	Grid,
	IconButton,
	InputAdornment,
	List,
	ListItem,
	ListItemText,
	Select,
	TextField,
	Typography,
} from "@mui/material";
import SwitchUI from "@mui/material/Switch";
import { useConfirm } from "material-ui-confirm";
import DeleteIcon from "@mui/icons-material/Delete";

//providers/hooks
import { AuthContext } from "../auth/AuthProvider";
import { deleteAudio, getAudio, saveAudio } from "../helpers/idb";
import alarmSound from "../assets/alarm.mp3"; // Import default alarm sound
import pizzaSound from "../assets/pizza.mp3"; // Import pizza sound
import Config from "~/config";

interface SettingsProps {
	setClientSet: (v: any) => void;
}

const Settings = (props: SettingsProps) => {
	const pageTitle = "Settings";

	const pageButtons: any = [{}];

	const { setClientSet } = props;

	const dispatch = useAppDispatch();
	const confirm = useConfirm();

	const [profile, setProfile] = useState<string | null>(localStorage.getItem("profile") || "count");
	const [previewUrl, setPreviewUrl] = useState("");

	const themeType = useAppSelector((state) => state.theme.themeType);
	const alarmEnabled = useAppSelector((state) => state.settings.alarmEnabled);
	const alarmThreshold = useAppSelector((state) => state.settings.alarmThreshold);
	const defaultAlarmSound = useAppSelector((state) => state.settings.defaultAlarmSound);
	const [currentAlarmSound, setCurrentAlarmSound] = useState<string>(alarmSound);

	interface ExistingFile {
		blob: Blob;
		name: string;
	}

	const [existingFile, setExistingFile] = useState<ExistingFile | null>(null);
	const [message, setMessage] = useState("");

	const pageCallbacks = async () => {
		dispatch(updateThemeName("main"));
		dispatch(updatePageTitle(pageTitle));
		dispatch(updatePageButtons(pageButtons));
	};

	useEffect(() => {
		if (defaultAlarmSound === "pizza") {
			setCurrentAlarmSound(pizzaSound);
		} else {
			setCurrentAlarmSound(alarmSound);
		}

		if (!existingFile) {
			setPreviewUrl(defaultAlarmSound === "pizza" ? pizzaSound : alarmSound);
		}
	}, [defaultAlarmSound, existingFile]);

	useEffect(() => {
		pageCallbacks();
		const checkAudio = async () => {
			try {
				const audioBlob = await getAudio();
				if (audioBlob) {
					const url = URL.createObjectURL(audioBlob);
					setExistingFile({
						blob: audioBlob,
						name: audioBlob.name || "Uploaded Audio",
					});
					setPreviewUrl(url);
				} else {
					setExistingFile(null);
					setPreviewUrl(currentAlarmSound); // Use default alarm.mp3
				}
			} catch (error) {
				console.error("Error checking audio:", error);
				setMessage("Error checking for existing audio.");
			}
		};
		checkAudio();
		// Cleanup URL on unmount
		return () => {
			if (previewUrl && previewUrl !== currentAlarmSound) {
				URL.revokeObjectURL(previewUrl);
			}
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const logout = async () => {
		authContext.redirectToLogout();
	};

	const handleThemeChange = (event: any) => {
		const { checked } = event.target;
		if (checked) {
			dispatch(setThemeTypeDark());
		} else {
			dispatch(setThemeTypeLight());
		}
	};

	const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
		const files = event.target.files;
		if (!files) {
			setMessage("No file selected.");
			return;
		}
		const file = files[0];
		if (file && file.type === "audio/mpeg") {
			try {
				await saveAudio(file);
				// Revoke old URL if it was an uploaded file
				if (previewUrl && previewUrl !== currentAlarmSound) {
					URL.revokeObjectURL(previewUrl);
				}
				const newUrl = URL.createObjectURL(file);
				setExistingFile({ blob: file, name: file.name });
				setPreviewUrl(newUrl);
				setMessage("");
			} catch (error) {
				console.error("Failed to save audio:", error);
				setMessage("Failed to upload audio file.");
			}
		} else {
			setMessage("Please upload a valid MP3 file.");
		}
	};

	const handleDelete = async () => {
		if (!existingFile) {
			setMessage("No audio file to delete.");
			return;
		}
		confirm({
			title: "Delete Audio File",
			description: "Delete audio file and use default alarm sound?",
			confirmationText: "Delete",
			cancellationText: "Cancel",
			confirmationButtonProps: { color: "error" },
			cancellationButtonProps: { color: "inherit" },
		})
			.then(() => {
				try {
					deleteAudio();
					// Revoke old URL if it was an uploaded file
					if (previewUrl && previewUrl !== currentAlarmSound) {
						URL.revokeObjectURL(previewUrl);
					}
					setExistingFile(null);
					setPreviewUrl(currentAlarmSound); // Switch to default

					setMessage("");
				} catch (error) {
					console.error("Failed to delete audio:", error);
					setMessage("Failed to remove audio file.");
				}
			})
			.catch(() => {});
	};

	const authContext = useContext(AuthContext);

	let currentProfile = authContext.profiles?.mobileApps?.find((p) => p.name === profile);

	return (
		<Grid container alignItems="stretch" direction="row" spacing={2} sx={{ padding: "20px" }}>
			{authContext.currentUser ? (
				<>
					<Grid item xs={12} sm={6} md={4}>
						<Typography variant="h6" gutterBottom={true}>
							User Info:
						</Typography>
						<dl>
							<dd>
								<b>Name:</b> {authContext.currentUser.firstName} {authContext.currentUser.lastName}
							</dd>
							<dd>
								<b>Email:</b> {authContext.currentUser.email}
							</dd>
							<dd>
								<b>Company:</b> {authContext.company?.name}
							</dd>
						</dl>
					</Grid>
					<Grid item xs={12} sm={6} md={4}>
						<Typography variant="h6" gutterBottom={true}>
							Graph endpoint:
						</Typography>
						<dl>
							<dd>{currentProfile?.graphUrl}</dd>
						</dl>
					</Grid>
					<Grid item xs={12} sm={6} md={4}>
						<Typography variant="h6" gutterBottom={true}>
							Profile:
						</Typography>
						<FormControl fullWidth={true}>
							<Select
								native={true}
								name="Vendor"
								variant="outlined"
								value={profile}
								onChange={(event) => {
									localStorage.setItem("profile", event.target.value as string);
									setProfile(event.target.value as string);
									setClientSet(false);
								}}
								inputProps={{
									name: "Vendor",
									id: "Vendor-input",
								}}
							>
								<option value="">Select Profile</option>
								{authContext.profiles?.mobileApps?.map((p) => (
									<option key={p.name} value={p.name}>
										{p.name}
									</option>
								))}
							</Select>
						</FormControl>
					</Grid>
				</>
			) : null}
			<Grid item xs={12} sm={6} md={4}>
				<Typography variant="h6" gutterBottom={true}>
					Dark Theme
				</Typography>
				<SwitchUI checked={themeType === "dark"} onChange={handleThemeChange} />
			</Grid>
			<Grid item xs={12} sx={{ borderBottom: 1, borderColor: "divider" }}>
				<h2>Alarm Settings</h2>
			</Grid>
			<Grid item xs={12} sm={6} md={4}>
				<Typography variant="h6" gutterBottom={true}>
					Enabled
				</Typography>
				<SwitchUI
					checked={alarmEnabled}
					onChange={(event) => {
						const { checked } = event.target;
						dispatch(updateAlarmEnabled(checked));
					}}
				/>
			</Grid>
			<Grid item xs={12} sm={6} md={4}>
				<Typography variant="h6" gutterBottom={true}>
					Alarm Threshold
				</Typography>
				<FormControl fullWidth={true}>
					<TextField
						type="number"
						disabled={!alarmEnabled}
						value={alarmThreshold || ""}
						onChange={(event) => {
							const value = event.target.value;
							if (value === "") {
								dispatch(updateAlarmThreshold(0));
							} else {
								const intValue = Math.floor(Number(value));
								if (intValue < 0) {
									dispatch(updateAlarmThreshold(0));
								} else if (intValue > 1000) {
									dispatch(updateAlarmThreshold(1000));
								} else {
									dispatch(updateAlarmThreshold(intValue));
								}
							}
						}}
						variant="outlined"
						inputProps={{
							min: 0,
							max: 1000,
							step: 1,
						}}
						InputLabelProps={{
							shrink: true,
						}}
						InputProps={{
							endAdornment: <InputAdornment position="end">%</InputAdornment>,
						}}
						helperText={alarmEnabled ? "Activate alarm whan count is this % of target" : "Alarm disabled"}
					/>
				</FormControl>
			</Grid>
			<Grid item xs={12} sm={6} md={4}>
				<Typography variant="h6" gutterBottom={true}>
					Alarm Sound {!existingFile && defaultAlarmSound === "pizza" && <>🍕</>}
				</Typography>
				{message && <p style={{ color: message.includes("Failed") ? "red" : "green" }}>{message}</p>}
				{existingFile ? (
					<TextField
						fullWidth
						value={existingFile.name}
						label="Current Custom Audio File"
						InputProps={{
							readOnly: true,
							endAdornment: (
								<InputAdornment position="end">
									<IconButton color="secondary" onClick={handleDelete} disabled={!existingFile}>
										<DeleteIcon />
									</IconButton>
								</InputAdornment>
							),
						}}
						variant="outlined"
					/>
				) : (
					<>
						<p>No current file, using default alarm sound</p>
						<p style={{ fontSize: "smaller" }}>
							Upload an MP3 file to use as an alarm sound.
							<br />
						</p>
						<input
							type="file"
							accept="audio/mpeg"
							onChange={handleFileChange}
							style={{
								borderWidth: "1px",
								borderStyle: "solid",
								borderColor: "#ccc",
								padding: "10px",
							}}
						/>
					</>
				)}
				<Divider sx={{ marginTop: "20px" }} />
				<p>Preview current alarm sound:</p>
				<audio controls src={previewUrl} style={{ width: "100%", maxWidth: "300px" }}>
					Your browser does not support the audio element.
				</audio>
				<Box
					style={{ marginTop: "10px", height: "80px" }}
					onClick={() => {
						let clickCount = Number(localStorage.getItem("alarmClickCount") || 0) + 1;
						localStorage.setItem("alarmClickCount", clickCount.toString());
						if (clickCount === 5) {
							localStorage.setItem("alarmClickCount", "0");
							if (defaultAlarmSound === "pizza") {
								dispatch(updateDefaultAlarmSound("default"));
							} else {
								dispatch(updateDefaultAlarmSound("pizza"));
							}
						}
					}}
				></Box>
			</Grid>
			<Grid item xs={12} sx={{ borderBottom: 1, borderColor: "divider" }}></Grid>
			<Grid item xs={12} sm={6} md={4}>
				{" "}
				<Typography variant="h6">Version</Typography>
				<List>
					<ListItem>
						<ListItemText>
							<b>Build</b>
							<br /> {Config.buildVersion}
						</ListItemText>
					</ListItem>
					<ListItem>
						<ListItemText>
							<b>Release</b>
							<br /> {Config.releaseVersion}
						</ListItemText>
					</ListItem>
				</List>
			</Grid>
			<Grid item xs={12} sm={6} md={4} alignItems={"center"} display={"flex"} justifyContent={"center"}>
				<Button color="secondary" variant="contained" fullWidth={true} onClick={() => logout()}>
					Sign Out
				</Button>
			</Grid>
			<Grid item xs={12}>
				<p style={{ fontSize: "smaller", marginTop: "90px" }}>{navigator.userAgent}</p>
			</Grid>
		</Grid>
	);
};
export default Settings;
